import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useMutation } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import RelayDataID from '@atlassian/relay-data-id';

import { deleteCustomPageExperience } from './constants';
import messages from './messages';

const TRACK_EVENT_TYPE = 'track';

export interface DeletePageModalProps {
	cloudId: string;
	helpCenterAri: string;
	helpCenterPageAri: string;
	isOpen: boolean;
	name: string;
	onClose: () => void;
	onSuccess?: () => void;
	showErrorFlag: (
		titleMessage: { defaultMessage: string; id: string },
		descriptionMessage?: { defaultMessage: string; id: string },
		messageValues?: Record<string, string>,
	) => void;
	showSuccessFlag: (
		titleMessage: { defaultMessage: string; id: string },
		messageValues?: Record<string, string>,
	) => void;
}

export const DeletePageModal = ({
	cloudId,
	isOpen,
	onClose,
	onSuccess,
	name,
	helpCenterAri,
	helpCenterPageAri,
	showErrorFlag,
	showSuccessFlag,
}: DeletePageModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isDeleting, setIsDeleting] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [commitDeletePage] = useMutation(graphql`
		mutation mainDeletePageMutation($cloudId: ID!, $input: HelpCenterPageDeleteInput!) {
			helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
				deleteHelpCenterPage(input: $input) {
					success
					errors {
						message
					}
				}
			}
		}
	`);

	const onDelete = useCallback(() => {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		deleteCustomPageExperience.start();

		setIsDeleting(true);

		commitDeletePage({
			variables: {
				cloudId,
				input: {
					helpCenterPageAri,
				},
			},
			updater: (store) => {
				const pageRelayId = RelayDataID({ id: helpCenterPageAri }, 'HelpCenterPage');
				if (!pageRelayId) {
					return;
				}

				// Access HelpCenter through the query path (like logo-upload-section does)
				const root = store.getRoot();
				const helpCenterField = root.getLinkedRecord('helpCenter', { cloudId });
				const helpCenterById = helpCenterField?.getLinkedRecord('helpCenterById', {
					helpCenterAri: helpCenterAri,
				});

				if (helpCenterById) {
					const pages = helpCenterById.getLinkedRecords('pages');
					if (pages) {
						const updatedPages = pages.filter((page) => page && page.getDataID() !== pageRelayId);
						helpCenterById.setLinkedRecords(updatedPages, 'pages');
					}
				}
			},
			onCompleted: (response: any) => {
				setIsDeleting(false);

				if (response?.helpCenter?.deleteHelpCenterPage?.success) {
					createAnalyticsEvent({
						helpCenterPageAri,
						analyticsType: TRACK_EVENT_TYPE,
						action: 'deleted',
						actionSubject: 'customPage',
					}).fire();

					showSuccessFlag(messages.deletePageModalModalSuccessTitle, { value: name });

					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					deleteCustomPageExperience.success();
					onClose();
					onSuccess?.();
				} else {
					const errorMessage =
						response?.helpCenter?.deleteHelpCenterPage?.errors?.[0]?.message || '';
					showErrorFlag(
						messages.deletePageModalModalErrorTitle,
						messages.deletePageModalModalErrorDesc,
						{ value: name },
					);

					createAnalyticsEvent({
						helpCenterPageAri,
						analyticsType: TRACK_EVENT_TYPE,
						action: 'delete_failed',
						actionSubject: 'customPage',
					}).fire();

					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					deleteCustomPageExperience.failure({
						metadata: {
							error: errorMessage,
						},
					});
				}
			},
			onError: (error: Error) => {
				setIsDeleting(false);
				showErrorFlag(
					messages.deletePageModalModalErrorTitle,
					messages.deletePageModalModalErrorDesc,
					{ value: name },
				);

				createAnalyticsEvent({
					helpCenterPageAri,
					analyticsType: TRACK_EVENT_TYPE,
					action: 'delete_failed',
					actionSubject: 'customPage',
				}).fire();

				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				deleteCustomPageExperience.failure({
					metadata: {
						error: error.message,
					},
				});
			},
		});
	}, [
		cloudId,
		helpCenterAri,
		helpCenterPageAri,
		commitDeletePage,
		createAnalyticsEvent,
		showSuccessFlag,
		name,
		onClose,
		onSuccess,
		showErrorFlag,
	]);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal width="small" onClose={onClose}>
					<ModalHeader>
						<ModalTitle appearance="warning" testId="page-list-delete-modal-title">
							{formatMessage(messages.deletePageModalTitle, { value: name })}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>{formatMessage(messages.deletePageModalModalDesc, { value: name })}</ModalBody>
					<ModalFooter>
						<Button onClick={onClose} appearance="subtle">
							{formatMessage(messages.deletePageModalModalCancelButton)}
						</Button>
						<Button
							onClick={onDelete}
							isLoading={isDeleting}
							appearance="warning"
							testId="page-list-delete-modal-delete-button"
						>
							{formatMessage(messages.deletePageModalModalConfirmButton)}
						</Button>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};

